/**
 * Serverless function templates for Stripe integration
 * These templates can be injected into user projects for secure payment processing
 */

/**
 * Netlify Function: Create Payment Intent
 * Handles server-side payment intent creation
 */
export const netlifyCreatePaymentIntent = `
import Stripe from 'stripe';

const stripe = new Stripe(process.env.STRIPE_SECRET_KEY!, {
  apiVersion: '2024-11-20.acacia',
});

export const handler = async (event) => {
  // Only allow POST
  if (event.httpMethod !== 'POST') {
    return {
      statusCode: 405,
      body: JSON.stringify({ error: 'Method not allowed' }),
    };
  }

  try {
    const { amount, currency = 'usd', metadata = {} } = JSON.parse(event.body || '{}');

    if (!amount || amount < 50) {
      return {
        statusCode: 400,
        body: JSON.stringify({ error: 'Amount must be at least 50 cents' }),
      };
    }

    const paymentIntent = await stripe.paymentIntents.create({
      amount,
      currency,
      metadata,
      automatic_payment_methods: {
        enabled: true,
      },
    });

    return {
      statusCode: 200,
      headers: {
        'Content-Type': 'application/json',
        'Access-Control-Allow-Origin': '*',
      },
      body: JSON.stringify({
        clientSecret: paymentIntent.client_secret,
        id: paymentIntent.id,
      }),
    };
  } catch (error) {
    console.error('Error creating payment intent:', error);
    return {
      statusCode: 500,
      body: JSON.stringify({ 
        error: error instanceof Error ? error.message : 'Failed to create payment intent' 
      }),
    };
  }
};
`;

/**
 * Netlify Function: Create Checkout Session
 * Redirects to Stripe Checkout
 */
export const netlifyCreateCheckoutSession = `
import Stripe from 'stripe';

const stripe = new Stripe(process.env.STRIPE_SECRET_KEY!, {
  apiVersion: '2024-11-20.acacia',
});

export const handler = async (event) => {
  if (event.httpMethod !== 'POST') {
    return {
      statusCode: 405,
      body: JSON.stringify({ error: 'Method not allowed' }),
    };
  }

  try {
    const { priceId, quantity = 1, successUrl, cancelUrl } = JSON.parse(event.body || '{}');

    if (!priceId) {
      return {
        statusCode: 400,
        body: JSON.stringify({ error: 'Price ID is required' }),
      };
    }

    const session = await stripe.checkout.sessions.create({
      mode: 'payment',
      line_items: [
        {
          price: priceId,
          quantity,
        },
      ],
      success_url: successUrl || \`\${event.headers.origin}/success?session_id={CHECKOUT_SESSION_ID}\`,
      cancel_url: cancelUrl || \`\${event.headers.origin}/cancel\`,
    });

    return {
      statusCode: 200,
      headers: {
        'Content-Type': 'application/json',
        'Access-Control-Allow-Origin': '*',
      },
      body: JSON.stringify({
        url: session.url,
        id: session.id,
      }),
    };
  } catch (error) {
    console.error('Error creating checkout session:', error);
    return {
      statusCode: 500,
      body: JSON.stringify({ 
        error: error instanceof Error ? error.message : 'Failed to create checkout session' 
      }),
    };
  }
};
`;

/**
 * Netlify Function: Create Subscription
 * Creates a subscription for recurring payments
 */
export const netlifyCreateSubscription = `
import Stripe from 'stripe';

const stripe = new Stripe(process.env.STRIPE_SECRET_KEY!, {
  apiVersion: '2024-11-20.acacia',
});

export const handler = async (event) => {
  if (event.httpMethod !== 'POST') {
    return {
      statusCode: 405,
      body: JSON.stringify({ error: 'Method not allowed' }),
    };
  }

  try {
    const { priceId, customerId, metadata = {} } = JSON.parse(event.body || '{}');

    if (!priceId || !customerId) {
      return {
        statusCode: 400,
        body: JSON.stringify({ error: 'Price ID and customer ID are required' }),
      };
    }

    const subscription = await stripe.subscriptions.create({
      customer: customerId,
      items: [{ price: priceId }],
      metadata,
      payment_behavior: 'default_incomplete',
      payment_settings: { save_default_payment_method: 'on_subscription' },
      expand: ['latest_invoice.payment_intent'],
    });

    return {
      statusCode: 200,
      headers: {
        'Content-Type': 'application/json',
        'Access-Control-Allow-Origin': '*',
      },
      body: JSON.stringify({
        subscriptionId: subscription.id,
        clientSecret: subscription.latest_invoice?.payment_intent?.client_secret,
      }),
    };
  } catch (error) {
    console.error('Error creating subscription:', error);
    return {
      statusCode: 500,
      body: JSON.stringify({ 
        error: error instanceof Error ? error.message : 'Failed to create subscription' 
      }),
    };
  }
};
`;

/**
 * Netlify Function: Webhook Handler
 * Handles Stripe webhook events
 */
export const netlifyWebhookHandler = `
import Stripe from 'stripe';

const stripe = new Stripe(process.env.STRIPE_SECRET_KEY!, {
  apiVersion: '2024-11-20.acacia',
});

const webhookSecret = process.env.STRIPE_WEBHOOK_SECRET!;

export const handler = async (event) => {
  if (event.httpMethod !== 'POST') {
    return {
      statusCode: 405,
      body: JSON.stringify({ error: 'Method not allowed' }),
    };
  }

  const sig = event.headers['stripe-signature'];

  if (!sig) {
    return {
      statusCode: 400,
      body: JSON.stringify({ error: 'No signature provided' }),
    };
  }

  try {
    const stripeEvent = stripe.webhooks.constructEvent(
      event.body!,
      sig,
      webhookSecret
    );

    // Handle different event types
    switch (stripeEvent.type) {
      case 'payment_intent.succeeded':
        const paymentIntent = stripeEvent.data.object;
        console.log('PaymentIntent succeeded:', paymentIntent.id);
        // TODO: Fulfill the order
        break;

      case 'payment_intent.payment_failed':
        const failedPayment = stripeEvent.data.object;
        console.log('PaymentIntent failed:', failedPayment.id);
        // TODO: Handle failed payment
        break;

      case 'checkout.session.completed':
        const session = stripeEvent.data.object;
        console.log('Checkout session completed:', session.id);
        // TODO: Fulfill the order
        break;

      case 'customer.subscription.created':
      case 'customer.subscription.updated':
      case 'customer.subscription.deleted':
        const subscription = stripeEvent.data.object;
        console.log('Subscription event:', stripeEvent.type, subscription.id);
        // TODO: Update subscription status
        break;

      default:
        console.log('Unhandled event type:', stripeEvent.type);
    }

    return {
      statusCode: 200,
      body: JSON.stringify({ received: true }),
    };
  } catch (error) {
    console.error('Webhook error:', error);
    return {
      statusCode: 400,
      body: JSON.stringify({ 
        error: error instanceof Error ? error.message : 'Webhook handler failed' 
      }),
    };
  }
};
`;

/**
 * Vercel Edge Function: Create Payment Intent
 */
export const vercelCreatePaymentIntent = `
import Stripe from 'stripe';

const stripe = new Stripe(process.env.STRIPE_SECRET_KEY!, {
  apiVersion: '2024-11-20.acacia',
});

export const config = {
  runtime: 'edge',
};

export default async function handler(request: Request) {
  if (request.method !== 'POST') {
    return new Response(JSON.stringify({ error: 'Method not allowed' }), {
      status: 405,
      headers: { 'Content-Type': 'application/json' },
    });
  }

  try {
    const { amount, currency = 'usd', metadata = {} } = await request.json();

    if (!amount || amount < 50) {
      return new Response(
        JSON.stringify({ error: 'Amount must be at least 50 cents' }),
        { status: 400, headers: { 'Content-Type': 'application/json' } }
      );
    }

    const paymentIntent = await stripe.paymentIntents.create({
      amount,
      currency,
      metadata,
      automatic_payment_methods: {
        enabled: true,
      },
    });

    return new Response(
      JSON.stringify({
        clientSecret: paymentIntent.client_secret,
        id: paymentIntent.id,
      }),
      {
        status: 200,
        headers: {
          'Content-Type': 'application/json',
          'Access-Control-Allow-Origin': '*',
        },
      }
    );
  } catch (error) {
    console.error('Error creating payment intent:', error);
    return new Response(
      JSON.stringify({ 
        error: error instanceof Error ? error.message : 'Failed to create payment intent' 
      }),
      { status: 500, headers: { 'Content-Type': 'application/json' } }
    );
  }
}
`;

/**
 * Get serverless function template by platform and type
 */
export function getServerlessTemplate(
  platform: 'netlify' | 'vercel',
  type: 'payment-intent' | 'checkout-session' | 'subscription' | 'webhook'
): string {
  const templates = {
    netlify: {
      'payment-intent': netlifyCreatePaymentIntent,
      'checkout-session': netlifyCreateCheckoutSession,
      'subscription': netlifyCreateSubscription,
      'webhook': netlifyWebhookHandler,
    },
    vercel: {
      'payment-intent': vercelCreatePaymentIntent,
      'checkout-session': netlifyCreateCheckoutSession, // Adapt as needed
      'subscription': netlifyCreateSubscription, // Adapt as needed
      'webhook': netlifyWebhookHandler, // Adapt as needed
    },
  };

  return templates[platform][type];
}

/**
 * Get the directory structure for serverless functions
 */
export function getServerlessFunctionPath(
  platform: 'netlify' | 'vercel',
  functionName: string
): string {
  const paths = {
    netlify: `netlify/functions/${functionName}.ts`,
    vercel: `api/${functionName}.ts`,
  };

  return paths[platform];
}

/**
 * Package.json dependencies needed for Stripe serverless functions
 */
export const stripeDependencies = {
  stripe: '^17.3.1',
  '@types/node': '^22.10.2',
};
